import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  ButtonBase,
  IconProps,
  Popover,
  Typography,
  TypographyProps,
} from '@mui/material';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TP_COLORS } from '../../../../constants/Color';
import { useAppSelector } from '../../../../hooks';
import { getSubTopicState } from '../../../../store/SubTopicSlice';
import {
  ISubjectForProgress,
  getProgressState,
} from '../../../../store/ProgressSlice';

import CircularProgressWithValue from './CircularProgressWithValue';

const progressTextProps: TypographyProps = {
  variant: 'subtitle2',
  style: {
    color: TP_COLORS.white,
    marginLeft: '5px',
  },
};

const subjectProgressTextProps: TypographyProps = {
  variant: 'subtitle2',
  style: {
    fontWeight: '600',
  },
};

const subjectProgressText2Props: TypographyProps = {
  variant: 'subtitle2',
  style: {
    opacity: 0.6,
  },
};

const iconProps: IconProps['sx'] = {
  fontSize: 'small',
  style: {
    color: TP_COLORS.white,
    height: '17px',
    marginTop: '5px',
  },
};

const containerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
};

const boxProps: BoxProps = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CircularProgressWithContent = ({ content, value }) => {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgressWithValue
        value={value || 0}
        progressProps={{ color: TP_COLORS.white }}
        thickness={4}
        circleColor="#c1c1c1"
      />
      <Box {...boxProps}>
        <Typography variant="caption" component="div" color="textSecondary">
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

interface IState {
  totalSubTopics: number;
  completedSubTopics: number;
}

const SubjectProgress: React.FC = () => {
  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const progressState = useAppSelector(getProgressState);

  const { subTopic, groupedTopicsWithSubTopicsData } = subTopicState;
  const { progress } = progressState;

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [state, setState] = useState<IState>({
    totalSubTopics: 0,
    completedSubTopics: 0,
  });
  const [subjectProgress, setSubjectProgress] = useState<
    ISubjectForProgress | undefined
  >(undefined);

  useEffect(() => {
    if (
      progress &&
      progress.subjects &&
      progress.subjects.length &&
      subTopic &&
      subTopic.topic
    ) {
      setSubjectProgress(
        progress.subjects.find(
          (item) =>
            item.subject === subTopic.topic.subject &&
            item.subjectCode === subTopic.topic.subjectCode &&
            item.subjectLevel === subTopic.topic.subjectLevel
        )
      );
    }
  }, [progress, subTopic]);

  useEffect(() => {
    if (
      subjectProgress &&
      subjectProgress.topics &&
      subjectProgress.topics.length
    ) {
      setState((prevState) => ({
        ...prevState,
        completedSubTopics: subjectProgress.topics.reduce(
          (accumulator, object) => {
            return (
              accumulator +
              object.subTopics.filter((item) => item.completion === 100).length
            );
          },
          0
        ),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        completedSubTopics: 0,
      }));
    }
  }, [subjectProgress]);

  useEffect(() => {
    if (
      groupedTopicsWithSubTopicsData &&
      groupedTopicsWithSubTopicsData.list &&
      groupedTopicsWithSubTopicsData.list.length
    ) {
      setState((prevState) => ({
        ...prevState,
        totalSubTopics: groupedTopicsWithSubTopicsData.list.reduce(
          (accumulator, object) => {
            return accumulator + object.subTopics.length;
          },
          0
        ),
      }));
    }
  }, [groupedTopicsWithSubTopicsData]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'progress-popover' : undefined;

  return (
    <>
      <div style={containerStyles}>
        <ButtonBase disableRipple onClick={handleClick}>
          <CircularProgressWithContent
            value={(state.completedSubTopics / state.totalSubTopics) * 100}
            content={<EmojiEventsIcon {...iconProps} />}
          />

          <Typography {...progressTextProps}>Your Progress</Typography>
          <ExpandMoreIcon style={{ color: TP_COLORS.white }} />
        </ButtonBase>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{ marginTop: '5px' }}
      >
        <div style={{ margin: '15px', width: '200px' }}>
          <Typography {...subjectProgressTextProps}>
            {state.completedSubTopics} of {state.totalSubTopics} Complete
          </Typography>
          <Typography {...subjectProgressText2Props}>
            {state.completedSubTopics} sub-topics completed from{' '}
            {subTopic?.topic.curriculum} {subTopic?.topic.subject}
          </Typography>
        </div>
      </Popover>
    </>
  );
};

export default SubjectProgress;
